import { useState } from "react";
import { subscriptionService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

export const useInspectionTemplateViewModel = ({ inspectionTemplateId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inspectionTemplate, setInspectionTemplate] = useState({});
  const [trades, setTrades] = useState([]);
  const stateManager = useComponentState();

  const getInspectionTemplateDetails = () => {
    stateManager.run(async () => {
      const response = await taskService.inspectionTemplates.getById({
        id: inspectionTemplateId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setInspectionTemplate(response.data);
      }
    });
  };

  const getTrades = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.trades.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        const updatedTrades = response.data.map(({ name, id }) => ({
          label: name,
          value: id,
        }));

        setTrades(updatedTrades);
      }
    });
  };

  const getAllAssignmentTemplates = () => {
    return stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getAll({
        sort: "name asc",
      });

      if (response.status === HTTP_STATUSES.OK) {
        const existingTemplateIds = new Set(
          inspectionTemplate?.assignmentTemplates?.map(({ id }) => id)
        );

        return response?.data?.filter(({ id }) => !existingTemplateIds.has(id));
      }

      return [];
    });
  };

  const handleAddAssignmentTemplate = ({ assignmentTemplateId, ...data }) => {
    stateManager.run(async () => {
      const { status } =
        await taskService.inspectionTemplateAssignmentTemplateMaps.create({
          data: {
            orderTemplateId: inspectionTemplateId,
            assignmentTemplateId,
            ...data,
          },
          toastMessageConfig: {
            success: {
              text: `${data.name} was added.`,
            },
          },
        });

      if (status === HTTP_STATUSES.OK) {
        closeModal();
        getInspectionTemplateDetails();
      }
    });
  };

  const onDetailsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = inspectionTemplate;

      const { status } = await taskService.inspectionTemplates.update({
        id: inspectionTemplateId,
        data: { ...data, modifiedOn },
        toastMessageConfig: {
          success: {
            text: `${name}'s details were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getInspectionTemplateDetails();
      }
    });
  };

  const onInputOptionsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = inspectionTemplate;
      const { status } = await taskService.inspectionTemplates.updateConfigById({
        id: inspectionTemplateId,
        data: { fields: data },
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${name}'s input options were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getInspectionTemplateDetails();
      }
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return {
    inspectionTemplate,
    loading: stateManager.isBusy(),
    isModalOpen,
    getAllAssignmentTemplates,
    handleAddAssignmentTemplate,
    closeModal,
    openModal,
    getInspectionTemplateDetails,
    onDetailsUpdate,
    onInputOptionsUpdate,
    getTrades,
    trades,
  };
};
